import { logWarning } from "./logger";

// Cache for Lottie metadata to avoid re-fetching
const lottieMetadataCache = new Map<
  string,
  { width: number; height: number }
>();

// Cache for fetched Lottie JSON data
const lottieJsonCache = new Map<string, object>();

export interface LottieDimensions {
  width: number;
  height: number;
  aspectRatio: number;
}

/**
 * Converts a hex color string to Lottie RGBA format [r, g, b, a] where values are 0-1
 */
export function hexToLottieColor(
  hex: string,
): [number, number, number, number] {
  // Remove # if present
  const cleanHex = hex.replace(/^#/, "");

  // Parse hex values
  const r = parseInt(cleanHex.substring(0, 2), 16) / 255;
  const g = parseInt(cleanHex.substring(2, 4), 16) / 255;
  const b = parseInt(cleanHex.substring(4, 6), 16) / 255;

  return [r, g, b, 1];
}

/**
 * Recursively replaces all color values in a Lottie JSON object.
 * Targets fill ("c") and stroke ("c") color properties.
 */
function replaceColorsInObject(
  obj: unknown,
  newColor: [number, number, number, number],
): unknown {
  if (obj === null || obj === undefined) return obj;

  if (Array.isArray(obj)) {
    return obj.map((item) => replaceColorsInObject(item, newColor));
  }

  if (typeof obj === "object") {
    const result: Record<string, unknown> = {};

    for (const [key, value] of Object.entries(obj as Record<string, unknown>)) {
      // Check if this is a color property ("c" key with "k" containing RGBA array)
      if (key === "c" && typeof value === "object" && value !== null) {
        const colorObj = value as Record<string, unknown>;
        if ("k" in colorObj) {
          const k = colorObj.k;
          // Check if k is an RGBA array [r, g, b, a]
          if (
            Array.isArray(k) &&
            k.length >= 3 &&
            k.length <= 4 &&
            typeof k[0] === "number" &&
            typeof k[1] === "number" &&
            typeof k[2] === "number"
          ) {
            result[key] = { ...colorObj, k: newColor };
            continue;
          }
          // Check for animated colors (array of keyframes)
          if (Array.isArray(k) && k.length > 0 && typeof k[0] === "object") {
            result[key] = {
              ...colorObj,
              k: k.map((keyframe: unknown) => {
                if (typeof keyframe === "object" && keyframe !== null) {
                  const kf = keyframe as Record<string, unknown>;
                  // Legacy bodymovin keyframes carry both a start ("s") and an
                  // end ("e") color. Replace each independently — returning
                  // early after "s" would leave "e" at the original color and
                  // tween away from the requested one.
                  const hasStart = "s" in kf && Array.isArray(kf.s);
                  const hasEnd = "e" in kf && Array.isArray(kf.e);
                  if (hasStart || hasEnd) {
                    const next = { ...kf };
                    if (hasStart) next.s = newColor;
                    if (hasEnd) next.e = newColor;
                    return next;
                  }
                }
                return keyframe;
              }),
            };
            continue;
          }
        }
      }

      result[key] = replaceColorsInObject(value, newColor);
    }

    return result;
  }

  return obj;
}

/**
 * Creates a colorized copy of a Lottie JSON object with all colors replaced.
 */
export function colorizeLottie(lottieJson: object, hexColor: string): object {
  const newColor = hexToLottieColor(hexColor);
  return replaceColorsInObject(lottieJson, newColor) as object;
}

/**
 * Check if a source path is a dotLottie (.lottie) file.
 * dotLottie files are ZIP archives and cannot be parsed as JSON directly.
 * Handles URLs with query/hash suffixes (e.g. anim.lottie?v=1 or anim.lottie#v2).
 */
export function isDotLottie(src: string): boolean {
  try {
    const pathname = new URL(src, "http://x").pathname;
    return pathname.toLowerCase().endsWith(".lottie");
  } catch {
    return src.toLowerCase().endsWith(".lottie");
  }
}

/**
 * Fetches a Lottie JSON file and returns the parsed JSON.
 * Results are cached to avoid repeated network requests.
 * Returns null for .lottie files (ZIP archives that can't be parsed as JSON).
 */
export async function fetchLottieJson(src: string): Promise<object | null> {
  // .lottie files are ZIP archives — cannot extract JSON without a dedicated parser
  if (isDotLottie(src)) {
    return null;
  }

  // Check cache first
  const cached = lottieJsonCache.get(src);
  if (cached) {
    return cached;
  }

  try {
    const response = await fetch(src);
    if (!response.ok) {
      logWarning(`Failed to fetch Lottie file: ${src}`);
      return null;
    }

    const json = await response.json();

    // Cache the result
    lottieJsonCache.set(src, json);

    return json;
  } catch (error) {
    logWarning(`Error fetching Lottie file: ${src}`, error);
    return null;
  }
}

/**
 * Fetches and parses Lottie JSON metadata to get intrinsic dimensions.
 * Results are cached to avoid repeated network requests.
 * Returns null for .lottie files (dimensions are read from the player instance instead).
 */
export async function getLottieDimensions(
  src: string,
): Promise<LottieDimensions | null> {
  // .lottie files are ZIP archives — dimensions should be read from the player instance
  if (isDotLottie(src)) {
    return null;
  }

  // Check cache first
  const cached = lottieMetadataCache.get(src);
  if (cached) {
    return {
      ...cached,
      aspectRatio: cached.width / cached.height,
    };
  }

  const json = await fetchLottieJson(src);
  if (!json) {
    return null;
  }

  // Lottie JSON has "w" and "h" properties for width and height
  const { w: width, h: height } = json as { w: unknown; h: unknown };

  if (typeof width !== "number" || typeof height !== "number") {
    logWarning(`Lottie file missing dimensions: ${src}`);
    return null;
  }

  // Cache the result
  lottieMetadataCache.set(src, { width, height });

  return {
    width,
    height,
    aspectRatio: width / height,
  };
}

/**
 * Clears the Lottie metadata cache (useful for testing or memory management)
 */
export function clearLottieCache(): void {
  lottieMetadataCache.clear();
}

/**
 * Parses an offset value that can be percentage, pixel string, or number.
 * @param value - "10%" | "50px" | 50
 * @param containerSize - The container dimension to calculate percentage from
 * @returns Pixel value as number
 */
export function parseOffsetValue(
  value: string | number | undefined,
  containerSize: number,
): number {
  if (value === undefined) return 0;

  if (typeof value === "number") {
    return value;
  }

  const trimmed = value.trim();

  // Percentage value
  if (trimmed.endsWith("%")) {
    const percent = parseFloat(trimmed.slice(0, -1));
    if (isNaN(percent)) return 0;
    return (percent / 100) * containerSize;
  }

  // Pixel value
  if (trimmed.endsWith("px")) {
    const pixels = parseFloat(trimmed.slice(0, -2));
    return isNaN(pixels) ? 0 : pixels;
  }

  // Plain number string
  const num = parseFloat(trimmed);
  return isNaN(num) ? 0 : num;
}
